import { useState } from 'react';
import { BellRing, ShieldAlert, Smartphone, X } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';

interface PushPermissionPromptProps {
  permission: NotificationPermission | 'unsupported';
  isRequesting: boolean;
  error: Error | null;
  onRequestPermission: () => void;
}

const DISMISS_KEY = 'mbbe_destek_push_prompt_dismissed';

function getDismissed() {
  if (typeof window === 'undefined') return false;
  return window.sessionStorage.getItem(DISMISS_KEY) === 'true';
}

export function PushPermissionPrompt({
  permission,
  isRequesting,
  error,
  onRequestPermission,
}: PushPermissionPromptProps) {
  const { t, language } = useLanguage();
  const [dismissed, setDismissed] = useState(getDismissed);

  const dismiss = () => {
    if (typeof window !== 'undefined') {
      window.sessionStorage.setItem(DISMISS_KEY, 'true');
    }
    setDismissed(true);
  };

  if (permission === 'unsupported') {
    return (
      <div className="rounded-lg border border-white/5 bg-slate-950/45 px-3 py-2 text-xs leading-relaxed text-slate-400">
        {t('push_unsupported')}
      </div>
    );
  }

  if (permission === 'granted') {
    return (
      <div className="flex items-center gap-2 rounded-lg border border-emerald-400/20 bg-emerald-500/10 px-3 py-2 text-xs text-emerald-100">
        <BellRing size={15} className="mt-0.5 shrink-0" />
        <span>{t('push_granted')}</span>
      </div>
    );
  }

  if (permission === 'denied') {
    return (
      <div className="flex items-center gap-2 rounded-lg border border-yellow-400/20 bg-yellow-500/10 px-3 py-2 text-xs text-yellow-100">
        <ShieldAlert size={15} className="shrink-0" />
        <span>{t('push_denied')}</span>
      </div>
    );
  }

  if (dismissed) return null;

  return (
    <div className="rounded-lg border border-brand-300/20 bg-brand-500/10 p-3">
      <div className="flex items-start gap-2">
        <Smartphone size={16} className="mt-0.5 shrink-0 text-brand-200" />
        <div className="min-w-0 flex-1">
          <div className="text-sm font-semibold text-white">{t('push_prompt_title')}</div>
          <p className="mt-0.5 text-xs leading-relaxed text-slate-400">
            {t('push_prompt_desc')}
          </p>
          {error && <p className="mt-1 text-xs text-red-300">{error.message}</p>}
        </div>
        <button
          type="button"
          onClick={dismiss}
          className="-mr-1 -mt-1 flex h-11 w-11 shrink-0 items-center justify-center rounded-md text-slate-500 transition-colors hover:bg-white/5 hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/60"
          aria-label={t('push_dismiss_aria')}
          title={language === 'tr' ? 'Kapat' : 'Close'}
        >
          <X size={14} />
        </button>
      </div>
      <div className="mt-3 flex items-center gap-2">
        <button
          type="button"
          onClick={onRequestPermission}
          disabled={isRequesting}
          className="inline-flex min-h-11 items-center justify-center rounded-md bg-brand-600 px-3 text-xs font-semibold text-white transition-colors hover:bg-brand-500 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/60 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {isRequesting ? t('push_waiting_permission') : t('push_enable')}
        </button>
        <button
          type="button"
          onClick={dismiss}
          className="inline-flex min-h-11 items-center justify-center rounded-md border border-white/10 px-3 text-xs font-semibold text-slate-400 transition-colors hover:border-white/20 hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/60"
        >
          {t('push_later')}
        </button>
      </div>
    </div>
  );
}
